import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, CheckCircle, XCircle, Clock, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { communicationService } from '../../../services/communicationService';

const ExecutionTracking = ({ campaign, onBack }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (campaign) {
      loadExecutions();
    }
  }, [campaign, currentPage, pageSize, filter]);

  const loadExecutions = async () => {
    setLoading(true);
    try {
      const filters = {
        page: currentPage,
        pageSize: pageSize
      };
      
      if (filter !== 'all') {
        filters.status = filter;
      }

      const result = await communicationService.getExecutionsByCampaign(campaign.id, filters);
      setExecutions(result.data || []);
      setTotalCount(result.total || 0);
      setTotalPages(Math.ceil((result.total || 0) / pageSize));
    } catch (error) {
      console.error('Error loading executions:', error);
      alert('Failed to load executions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SENT':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'DELIVERED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'READ':
        return <CheckCircle className="w-4 h-4 text-purple-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SENT':
        return 'bg-blue-100 text-blue-700';
      case 'DELIVERED':
        return 'bg-green-100 text-green-700';
      case 'FAILED':
        return 'bg-red-100 text-red-700';
      case 'READ':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Stats are calculated from total count, not just current page
  // For accurate stats, we'd need to fetch counts separately, but for now use current page data
  const stats = {
    total: totalCount,
    sent: executions.filter(e => e.status === 'SENT').length,
    delivered: executions.filter(e => e.status === 'DELIVERED').length,
    failed: executions.filter(e => e.status === 'FAILED').length,
    read: executions.filter(e => e.status === 'READ').length
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
            <p className="text-sm text-gray-600">Campaign Execution Details</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">Total</p>
            <p className="text-lg font-semibold text-gray-900">{stats.total}</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600">Sent</p>
            <p className="text-lg font-semibold text-blue-900">{stats.sent}</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-xs text-green-600">Delivered</p>
            <p className="text-lg font-semibold text-green-900">{stats.delivered}</p>
          </div>
          <div className="p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-red-600">Failed</p>
            <p className="text-lg font-semibold text-red-900">{stats.failed}</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-600">Read</p>
            <p className="text-lg font-semibold text-purple-900">{stats.read}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mt-4">
          <span className="text-sm text-gray-600">Filter:</span>
          {['all', 'SENT', 'DELIVERED', 'FAILED', 'PENDING'].map(status => (
            <button
              key={status}
              onClick={() => handleFilterChange(status)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Executions List */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : executions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No executions found</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {executions.map(execution => (
              <div
                key={execution.id}
                className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(execution.status)}`}>
                        {getStatusIcon(execution.status)}
                        {execution.status}
                      </span>
                      <span className="text-xs text-gray-500">{execution.message_type}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {execution.recipient_mobile || execution.recipient_email}
                    </p>
                    <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                      {execution.message_content}
                    </p>
                    {execution.error_message && (
                      <p className="text-xs text-red-600 mb-1">
                        Error: {execution.error_message}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {execution.sent_at && (
                        <span>Sent: {new Date(execution.sent_at).toLocaleString()}</span>
                      )}
                      {execution.delivered_at && (
                        <span>Delivered: {new Date(execution.delivered_at).toLocaleString()}</span>
                      )}
                      {execution.read_at && (
                        <span>Read: {new Date(execution.read_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs sm:text-sm text-gray-600">per page</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs sm:text-sm text-gray-700 px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExecutionTracking;

